import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import { motion } from 'framer-motion';

const COLUMNS = [
  {
    head: 'Visit',
    items: [
      { label: '9017 W. Pico Blvd.', to: '/contact-us' },
      { label: 'Los Angeles, CA 90035', to: '/contact-us' },
      { label: 'Open daily for shiurim', to: '/prayers' },
    ],
  },
  {
    head: 'Be in Touch',
    items: [
      { label: 'office@kbtla.org', to: '/contact-us' },
      { label: 'Venmo @KehilatBneiTorah', to: '/donate-now' },
      { label: 'Zelle bneitorah.la@gmail.com', to: '/donate-now' },
    ],
  },
  {
    head: 'Sections',
    items: [
      { label: 'Schedule', to: '/prayers' },
      { label: 'Classes & Shiurim', to: '/classes' },
      { label: 'Upcoming events', to: '/events' },
      { label: 'Membership', to: '/membership2026' },
    ],
  },
];

export default function EditorialFooter() {
  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        background: 'var(--navy-900)',
        color: 'var(--navy-100)',
        pt: { xs: 10, md: 14 },
        pb: 6,
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          right: '-6%',
          bottom: '-30%',
          fontFamily: 'var(--font-hebrew)',
          fontSize: 'min(80vw, 1200px)',
          color: '#fff',
          opacity: 0.025,
          lineHeight: 0.8,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        ש
      </Box>

      <Box sx={{ position: 'relative', maxWidth: 1280, mx: 'auto', px: { xs: 3, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={4}
          sx={{ mb: { xs: 8, md: 12 } }}
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <Stack spacing={1.5}>
              <Box
                component="img"
                src="/assets/images/kbt/kbt-logo-white.png"
                alt="KBT — Kehilat Bnei Torah"
                sx={{
                  width: { xs: 220, md: 320 },
                  height: 'auto',
                  display: 'block',
                  filter: 'drop-shadow(0 2px 12px rgba(0,0,0,0.25))',
                }}
              />
              <Box
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  letterSpacing: '0.22em',
                  color: 'var(--teal-300)',
                  textTransform: 'uppercase',
                  pl: 0.5,
                }}
              >
                More than just a Beit Knesset
              </Box>
            </Stack>
          </motion.div>

          <Box
            sx={{
              textAlign: { xs: 'left', md: 'right' },
              maxWidth: 460,
              fontFamily: 'var(--font-display)',
              fontWeight: 500,
              fontStyle: 'italic',
              fontSize: 'clamp(20px, 2vw, 26px)',
              lineHeight: 1.3,
              color: 'var(--navy-100)',
            }}
          >
            "Make for me a sanctuary, and I shall dwell among you."
            <Box
              sx={{
                mt: 1.5,
                fontFamily: 'var(--font-mono)',
                fontSize: 10.5,
                letterSpacing: '0.22em',
                color: 'var(--teal-300)',
                textTransform: 'uppercase',
                opacity: 0.85,
              }}
            >
              Shemot · 25:8
            </Box>
          </Box>
        </Stack>

        <Box sx={{ width: '100%', height: '2px', background: 'var(--teal-500)', opacity: 0.7, mb: { xs: 6, md: 8 } }} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: { xs: 5, md: 6 },
          }}
        >
          {COLUMNS.map((col) => (
            <Stack key={col.head} spacing={2}>
              <Box
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: 'var(--teal-300)',
                }}
              >
                {col.head}
              </Box>
              <Stack spacing={1.2}>
                {col.items.map((item) => (
                  <RouterLink key={item.label} to={item.to} style={{ textDecoration: 'none' }}>
                    <Box
                      sx={{
                        fontFamily: 'var(--font-display)',
                        fontWeight: 500,
                        fontSize: 18,
                        color: 'var(--navy-100)',
                        opacity: 0.9,
                        transition: 'opacity 300ms ease, color 300ms ease',
                        '&:hover': { opacity: 1, color: 'var(--teal-100)' },
                      }}
                    >
                      {item.label}
                    </Box>
                  </RouterLink>
                ))}
              </Stack>
            </Stack>
          ))}
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{
            mt: { xs: 10, md: 14 },
            pt: 4,
            borderTop: '1px solid rgba(255,255,255,0.12)',
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--navy-200)',
            opacity: 0.85,
          }}
        >
          <Box>© {new Date().getFullYear()}  ·  Kehilat Bnei Torah  ·  Los Angeles</Box>

          {/* Website credit — small Excersys mark, links out to excersys.com */}
          <Stack
            component="a"
            href="https://excersys.com"
            target="_blank"
            rel="noopener noreferrer"
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              opacity: 0.9,
              transition: 'opacity 250ms ease',
              '&:hover': { opacity: 1 },
            }}
          >
            <Box component="span">Website by</Box>
            <Box
              component="img"
              src="/assets/images/excersys-logo.webp"
              alt="Excersys"
              sx={{
                height: 18,
                width: 'auto',
                display: 'block',
                borderRadius: '2px',
              }}
            />
          </Stack>

          <Stack direction="row" spacing={3}>
            <Box
              component={RouterLink}
              to="/privacy"
              sx={{
                color: 'inherit',
                textDecoration: 'none',
                opacity: 0.9,
                transition: 'opacity 250ms ease, color 250ms ease',
                '&:hover': { opacity: 1, color: 'var(--teal-300)' },
              }}
            >
              Privacy
            </Box>
            <Box
              component={RouterLink}
              to="/terms"
              sx={{
                color: 'inherit',
                textDecoration: 'none',
                opacity: 0.9,
                transition: 'opacity 250ms ease, color 250ms ease',
                '&:hover': { opacity: 1, color: 'var(--teal-300)' },
              }}
            >
              Terms
            </Box>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
